<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <PrimeDialog
    :header="$t(actionType === 'copy' ? 'copyNode' : 'moveNode')"
    visible
    modal
    class="popup popup--tree"
    @keyup.esc="closePopup(false)"
  >
    <template #closeicon>
      <PrimeButton
        icon="ri-close-line"
        :aria-label="$t('close')"
        outlined
        text
        severity="secondary"
        autofocus="false"
        @click="closePopup(false)"
      />
    </template>
    <PrimeMessage v-if="displayError" :closable="false" severity="error">
      {{ actionType === "copy" ? errorCopy : errorMove }}
    </PrimeMessage>
    <div class="copy-move-layout">
      <div class="copy-move-source">
        <div class="copy-move-title">
          {{
            actionType === "copy"
              ? "Copier les éléments suivants"
              : "Déplacer les éléments suivants"
          }}
        </div>
        <PrimeDataView :value="nodes">
          <template #list="slotProps">
            <div class="copy-move-list">
              <div
                v-for="item in slotProps.items"
                :key="item.name"
                class="copy-move-item"
              >
                <span class="copy-move-item-content">
                  <i v-if="item.isFolder" class="ri-folder-2-line" />
                  <i v-else class="ri-file-line" />
                  <span class="copy-move-item-name">{{ item.name }}</span>
                </span>
              </div>
            </div>
          </template>
        </PrimeDataView>
      </div>
      <div class="copy-move-destination">
        <label class="copy-move-label">
          {{ $t("chooseDestination") }} <span class="required">*</span>
        </label>
        <TreeExplorer
          :current-node="currentNode"
          class="tree"
          :show-user-home="showUserHome"
          @select-node="selectNode"
        />
      </div>
      <label v-if="destination" class="copy-move-status">
        {{ $t("destination", { dest: destinationName }) }}
      </label>
      <label v-else class="copy-move-status">{{ $t("noDestination") }}</label>
    </div>
    <template #footer>
      <PrimeButton
        :label="$t('cancel')"
        severity="secondary"
        @click="closePopup"
      />
      <PrimeButton
        :label="$t(actionType === 'copy' ? 'copy' : 'move')"
        :disabled="!canPerformAction"
        @click="handleAction"
      />
    </template>
  </PrimeDialog>
</template>

<script>
import { useSelectionStore } from "@/stores/selection.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";
import TreeExplorer from "../TreeExplorer.vue";
import Button from "primevue/button";
import DataView from "primevue/dataview";
import Message from "primevue/message";
import Dialog from "primevue/dialog";

const SITE = "st:site";

export default {
  name: "CopyOrMovePopup",
  components: {
    TreeExplorer,
    PrimeButton: Button,
    PrimeDataView: DataView,
    PrimeMessage: Message,
    PrimeDialog: Dialog,
  },
  inject: ["actionsService"],
  props: {
    currentNode: {
      type: Object,
      default: () => ({}),
    },
    actionType: {
      type: String,
      default: "copy",
    },
    showUserHome: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close"],
  data() {
    return {
      nodes: [],
      destination: null,
      destinationName: null,
      documentLibrary: null,
      displayError: false,
      canPerformAction: true,
      errorCopy: null,
      errorMove: null,
    };
  },
  created() {
    this.selectionStore = useSelectionStore();
  },
  mounted() {
    this.nodes = this.selectionStore.nodes;
    this.displayError = false;
    this.destination = null;
    this.documentLibrary = null;
    this.destinationName = null;
    this.canPerformAction = true;
    this.errorCopy = null;
    this.errorMove = null;
  },
  methods: {
    closePopup(reload) {
      this.$emit("close", reload);
    },
    selectNode(item, label) {
      this.displayError = false;
      this.canPerformAction = true;
      this.errorCopy = null;
      this.errorMove = null;
      this.destination = item;
      this.destinationName = label;

      if (!this.canWrite(item)) {
        if (this.actionType === "copy") {
          this.errorCopy = this.$t("errors.errorMoveRights");
          this.displayError = true;
        } else {
          this.errorMove = this.$t("errors.errorMoveRights");
          this.displayError = true;
        }
        this.canPerformAction = false;
      }
    },
    async handleAction() {
      if (this.destination === null) {
        if (this.actionType === "copy") {
          this.errorCopy = this.$t("noSelectedDestination");
        } else {
          this.errorMove = this.$t("noSelectedDestination");
        }
        this.displayError = true;
        return;
      }
      let canDrop = null;
      if (this.actionsService?.canDropInDestination(this.destination)) {
        canDrop = this.actionsService.canDropInDestination(this.destination);
      } else {
        canDrop = true;
      }

      if (!canDrop) {
        if (this.actionType === "copy") {
          this.errorCopy = this.$t("cannotCopyHere");
        } else {
          this.errorMove = this.$t("cannotMoveHere");
        }
        this.displayError = true;
      } else {
        this.handleNodeAction();
      }
    },

    handleNodeAction() {
      let destId =
        this.destination.nodeType === SITE
          ? this.documentLibrary.id
          : this.destination.id;

      const actionPromises = this.nodes.map((element) => {
        return this.getItemName(destId, element).then((itemName) => {
          if (this.actionType === "copy") {
            return alfrescoNodeService.copyNode(element.id, destId, itemName);
          } else {
            return alfrescoNodeService.moveNode(element.id, destId, itemName);
          }
        });
      });

      Promise.allSettled(actionPromises)
        .then((responses) => {
          let successfulToast = responses
            .filter((resp) => resp.status === "fulfilled")
            .map((resp) => resp.value.entry?.name);
          let listError = responses
            .filter((resp) => resp.status !== "fulfilled")
            .map((resp) => resp.reason);

          if (successfulToast.length > 0) {
            this.selectionStore.resetSelection();
            this.$toast.add({
              severity: "success",
              summary: this.$t("success"),
              detail: this.$t(
                this.actionType === "copy"
                  ? "node.successCopy"
                  : "node.successMove",
                { number: successfulToast.length },
              ),
              life: 5000,
            });
            this.closePopup();
          }
          if (listError.length > 0) {
            this.$toast.add({
              severity: "error",
              summary: this.$t(
                this.actionType === "copy"
                  ? "errors.errorCopy"
                  : "errors.errorMove",
              ),
              detail: listError,
              life: 5000,
            });
          }
          this.closePopup(true);
        })
        .catch((error) => {
          this.$toast.add({
            severity: "error",
            summary: this.$t(
              this.actionType === "copy"
                ? "errors.errorCopy"
                : "errors.errorMove",
            ),
            detail: error,
            life: 5000,
          });
        });
    },

    getItemName(destId, item) {
      return alfrescoNodeService
        .getChildren(destId, 200, 0, true)
        .then((data) => {
          let itemName = item.name;
          let destChildren = data.children;
          let initialItemName = item.isFile
            ? this.removeExtension(itemName)
            : itemName;

          let increment = destChildren.filter((child) =>
            child.name.startsWith(initialItemName),
          ).length;
          if (increment > 0) {
            itemName = item.isFile
              ? `${initialItemName}-${increment}.${this.getExtension(itemName)}`
              : `${initialItemName}-${increment}`;
          }
          return itemName;
        });
    },
    getExtension(fileName) {
      let initialFileName = fileName.split(".");
      return initialFileName[initialFileName.length - 1];
    },
    removeExtension(fileName) {
      return fileName.substring(0, fileName.lastIndexOf(".")) || fileName;
    },
    canWrite(item) {
      return !!item?.allowableOperations?.find((child) => child === "create");
    },
  },
};
</script>

<style lang="scss" scoped>
.copy-move-layout {
  display: grid;
  grid-template-columns: 1fr 3fr;
  gap: 1rem;
}

.copy-move-source {
  grid-column: 1;
}

.copy-move-destination {
  grid-column: 2;
}

.copy-move-status {
  grid-column: 1 / -1;
  margin-bottom: 0.5rem;
}

.copy-move-title {
  margin-bottom: 0.75rem;
  color: var(--p-text-color);
  font-weight: 600;
}

.copy-move-list {
  width: 100%;
}

.copy-move-item {
  margin-bottom: 0.5rem;
}

.copy-move-item-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--p-text-muted-color);
}

.copy-move-item-name {
  font-weight: 600;
}

.copy-move-label {
  display: block;
  width: 100%;
  margin-bottom: 0.5rem;
  color: var(--p-text-color);
  font-weight: 600;
}
</style>
